// The MPIN/OTP and UPI PIN sheets for the payment auth chain (§9), driven by
// usePaymentAuth. Rendered by Send money and Pay via UPI.
import PinPad from '@/components/common/auth/PinPad';
import OtpSheet from '@/components/common/auth/OtpSheet';
import type { PaymentAuthState } from './usePaymentAuth';
import { REAL_API_ENABLED } from '@/api/flags';

// The bank's MPIN is 4 digits; the demo data uses a 6-digit one. Never a choice.
const MPIN_LENGTH: 4 | 6 = REAL_API_ENABLED ? 4 : 6;

interface Props {
  authState: PaymentAuthState;
  approvalLine: string;
  pinError: string;
  pinResetKey: number;
  pinLength?: 4 | 6;
  onPinLengthChange?: (length: 4 | 6) => void;
  otpExpiry: number;
  otpSessionKey: number;
  onCompletePin: (pin: string) => void;
  onVerifyOtp: (code: string) => Promise<void>;
  onResendOtp: () => Promise<number>;
  onClose: () => void;
}

const PaymentAuthSheets = ({
  authState,
  approvalLine,
  pinError,
  pinResetKey,
  pinLength = 6,
  onPinLengthChange,
  otpExpiry,
  otpSessionKey,
  onCompletePin,
  onVerifyOtp,
  onResendOtp,
  onClose,
}: Props) => (
  <>
    <PinPad
      show={authState === 'MPIN'}
      variant="MPIN"
      title="Enter your MPIN"
      subtitle={approvalLine}
      error={pinError}
      resetKey={pinResetKey}
      pinLength={MPIN_LENGTH}
      allowLengthSelection={false}
      onComplete={onCompletePin}
      onClose={onClose}
    />
    <PinPad
      show={authState === 'UPI_SET'}
      variant="UPI"
      title="Set your UPI PIN"
      subtitle="Choose four or six digits. Avoid sequences, repeats or your date of birth."
      error={pinError}
      resetKey={pinResetKey}
      pinLength={pinLength}
      allowLengthSelection
      onPinLengthChange={onPinLengthChange}
      onComplete={onCompletePin}
      onClose={onClose}
    />
    <PinPad
      show={authState === 'UPI_CONFIRM'}
      variant="UPI"
      title="Confirm your UPI PIN"
      error={pinError}
      resetKey={pinResetKey}
      pinLength={pinLength}
      allowLengthSelection
      onPinLengthChange={onPinLengthChange}
      onComplete={onCompletePin}
      onClose={onClose}
    />
    <PinPad
      show={authState === 'UPI_PIN'}
      variant="UPI"
      title="Enter UPI PIN"
      subtitle={approvalLine}
      error={pinError}
      resetKey={pinResetKey}
      pinLength={pinLength}
      allowLengthSelection
      onPinLengthChange={onPinLengthChange}
      onComplete={onCompletePin}
      onClose={onClose}
    />
    <OtpSheet
      show={authState === 'OTP'}
      approvalLine={approvalLine}
      sessionKey={otpSessionKey}
      expiresInSeconds={otpExpiry}
      onVerify={onVerifyOtp}
      onResend={onResendOtp}
      onClose={onClose}
    />
  </>
);

export default PaymentAuthSheets;
